import { useCallback } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'
import { match } from 'ts-pattern'

import { nodesAtom, presentationAtom } from 'Automations/automationEditor/atoms'
import { useResolvedNodeDefinitions } from 'Automations/nodeDefinitions'
import { NodeType } from 'Automations/nodeDefinitions/types'
import {
  CONTRACT_EXTRACTION_BASE,
  INVOICE_SENT_BASE,
  QUOTE_APPROVAL_BASE
} from '../../templateBases'
import { createPresentationFromBaseTemplate } from '../../utils'

type TriggerTemplate =
  | 'QUOTE_REQUIRES_APPROVAL'
  | 'CONTRACT_UPLOADED'
  | 'INVOICE_SENT'

const getBaseTemplate = (template: TriggerTemplate) =>
  match(template)
    .with('QUOTE_REQUIRES_APPROVAL', () => QUOTE_APPROVAL_BASE)
    .with('CONTRACT_UPLOADED', () => CONTRACT_EXTRACTION_BASE)
    .with('INVOICE_SENT', () => INVOICE_SENT_BASE)
    .exhaustive()

export const useApplyTemplate = () => {
  const setPresentation = useSetAtom(presentationAtom)
  const nodes = useAtomValue(nodesAtom)
  const definitions = useResolvedNodeDefinitions()

  const applyTemplate = useCallback(
    (template: TriggerTemplate) => {
      setPresentation(
        createPresentationFromBaseTemplate(getBaseTemplate(template))
      )
    },
    [setPresentation]
  )

  const insertTemplate = useCallback(
    (template: TriggerTemplate, existingStartNodeId: string) => {
      const newPresentation = createPresentationFromBaseTemplate(
        getBaseTemplate(template)
      )

      const templateStartNode = newPresentation.nodes.find(node => {
        const def = definitions[node.type as NodeType]
        return def?.startNodeTriggerType != null
      })

      if (!templateStartNode) return

      const existingStartNode = nodes.find(n => n.id === existingStartNodeId)

      const updatedNodes = [
        {
          ...templateStartNode,
          id: existingStartNodeId,
          data: {
            ...templateStartNode.data,
            name: existingStartNode?.data.name ?? ''
          }
        },
        ...newPresentation.nodes.filter(n => n.id !== templateStartNode.id)
      ]

      const updatedEdges = newPresentation.edges.map(edge => ({
        ...edge,
        id: edge.id.replaceAll(templateStartNode.id, existingStartNodeId),
        source:
          edge.source === templateStartNode.id
            ? existingStartNodeId
            : edge.source,
        sourceHandle: edge.sourceHandle?.replaceAll(
          templateStartNode.id,
          existingStartNodeId
        ),
        target:
          edge.target === templateStartNode.id
            ? existingStartNodeId
            : edge.target,
        targetHandle: edge.targetHandle?.replaceAll(
          templateStartNode.id,
          existingStartNodeId
        )
      }))

      setPresentation({
        ...newPresentation,
        nodes: updatedNodes,
        edges: updatedEdges
      })
    },
    [setPresentation, nodes, definitions]
  )

  return { applyTemplate, insertTemplate }
}
